import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import isBrowser from '../../../util/isBrowser';
import {
  selectEditMode,
  useAppDispatch,
  useAppSelector,
} from '../../../store/hooks';
import { EditMode, setEditMode } from '../../../store/EditorSlice';
import useIsFirstRender from '../../../lib/useIsFirstRender';

interface UseEditModeProps {
  initialEdit?: string;
  canAccessCuesMode?: boolean;
}

const getValidEditMode = (
  queryParam: string | string[] | undefined
): EditMode | undefined => {
  switch (queryParam) {
    case 'cue':
    case 'cues':
      return EditMode.CUES;
    case 'script':
      return EditMode.SCRIPT;
    default:
      return undefined;
  }
};
/**
 * Returns the initial edit mode based on the query param and the initialEdit prop
 * Returns script mode if the query param and initialEdit prop is invalid
 */
const getInitialEditMode = (
  queryParam: string | string[] | undefined,
  initialEdit: string | undefined,
  allowCuesMode: boolean
): EditMode => {
  if (!allowCuesMode) return EditMode.SCRIPT;

  const fromInitialEdit = getValidEditMode(initialEdit);
  const fromQueryParam = getValidEditMode(queryParam);
  return fromQueryParam ?? fromInitialEdit ?? EditMode.SCRIPT;
};

const useEditMode = (props: UseEditModeProps = {}) => {
  const { initialEdit = undefined, canAccessCuesMode = false } = props;
  const router = useRouter();
  const dispatch = useAppDispatch();
  const editMode = useAppSelector(selectEditMode);
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (isFirstRender) {
      const initial = getInitialEditMode(
        router?.query?.edit,
        initialEdit,
        canAccessCuesMode
      );
      dispatch(setEditMode(initial));
    }
  }, [
    canAccessCuesMode,
    dispatch,
    initialEdit,
    isFirstRender,
    router?.query?.edit,
  ]);

  /**
   * Update editMode when router query changes
   */
  useEffect(() => {
    if (!canAccessCuesMode) return;

    const newEditMode = getValidEditMode(router?.query.edit);

    if (newEditMode) {
      dispatch(setEditMode(newEditMode));
    }
  }, [editMode, router?.query.edit, canAccessCuesMode, dispatch]);

  /**
   * Updates the edit mode in the router query and state after validating the mode
   */
  const onEditModeChange = useCallback(
    (mode: EditMode) => {
      const validatedMode = getValidEditMode(mode);
      const isValidPathname =
        router && router.pathname.includes(`/dashboard/studio`);
      if (!isBrowser || !isValidPathname || !validatedMode) return;

      if (router) {
        router.replace(
          {
            query: {
              ...router.query,
              edit: validatedMode,
            },
          },
          undefined,
          { shallow: true }
        );
      }
    },
    [router]
  );

  return {
    editMode,
    onEditModeChange,
    isScriptMode: editMode === EditMode.SCRIPT,
    isCuesMode: editMode === EditMode.CUES,
  };
};

export default useEditMode;
